<script setup lang="ts">
import type { ArgentoInsurancePaymentPeriodicities } from '~/config'
import type { PaymentInfo } from '~/layers/forms/models'
import { Coverage } from '~/layers/forms/models'

import type { AsyncDataRequestStatus } from '#app'

interface Props {
  coverage: Coverage
  paymentInfo?: PaymentInfo
  periodicity?: PropsTypesOf<typeof ArgentoInsurancePaymentPeriodicities>['value'] | null
  bg?: 'dark' | 'light'
  cardLayout?: 'horizontal' | 'vertical'
  contentLayout?: 'horizontal' | 'vertical'
  status?: CoveragePlanStatus | AsyncDataRequestStatus | null
}

const props = withDefaults(defineProps<Props>(), {
  paymentInfo: undefined,
  periodicity: null,
  bg: 'light',
  cardLayout: 'vertical',
  contentLayout: 'horizontal',
  status: 'success',
})

const { LocalConfigurationStore } = stores()
const associatedCompaniesInfo = LocalConfigurationStore.value?.associatedCompanies ?? []

const company = computed(() =>
  associatedCompaniesInfo.find(
    (c) => c.argentoInsuranceId === props.coverage.argentoInsuranceCompanyId
  )
)

const logoDinamicClasses = computed(() => ({
  'h-20 aspect-[2/1] md:aspect-[3/1]': props.cardLayout === 'horizontal',
  'aspect-[16/4] max-h-14': props.cardLayout === 'vertical',
}))

const { getTotalPayment, discountIsValid } = useCoveragePaymentSumary({
  amount: computed(() => props.coverage.grandTotal),
  discount: computed(() => props.paymentInfo),
})

const periodicity = computed(
  () => props.periodicity ?? Coverage.getPeriodicityList().default?.value
)
</script>

<template>
  <CardComponent
    :bg="bg"
    blur-bg
    :layout="cardLayout"
  >
    <template #header>
      <ImageComponent
        v-if="company?.logoWhiteUrl"
        class="object-cover"
        :class="logoDinamicClasses"
        :src="getCompanyLogo(company?.logoWhiteUrl)"
      />
    </template>

    <ProcessingComponent
      v-if="status === 'pending'"
      minimal
    />
    <div
      v-else-if="status === 'success'"
      class="flex h-full w-full items-center justify-center"
      :class="cardLayout === 'vertical' ? 'flex-col gap-4 py-4' : 'gap-4 md:gap-6'"
    >
      <template v-if="coverage.planName && $viewport.isGreaterThan('md')">
        <div class="flex flex-col text-center">
          <span class="leading-none"> — Plan — </span>
          <span
            class="font-bold uppercase leading-none"
            :class="{
              'text-2xl': coverage.planName.length > 14,
              'text-3xl': coverage.planName.length <= 14,
            }"
          >
            {{ coverage.planName }}
          </span>
        </div>
        <div class="self-stretch">
          <PvDivider :layout="cardLayout === 'vertical' ? 'horizontal' : 'vertical'" />
        </div>
      </template>
      <template v-if="discountIsValid">
        <div class="px-5 text-center">
          Descuento aplicado con código promocional
          <br />
          <span class="text-lg font-bold text-primary">#{{ paymentInfo?.discountTag }}</span>
        </div>
      </template>
      <CoveragePrice
        :amount="getTotalPayment()"
        :discount-amount="discountIsValid ? getTotalPayment({ applyDiscount: true }) : undefined"
        :periodicity
        :layout="contentLayout"
      />
    </div>
  </CardComponent>
</template>
